//@flow
import React, {useCallback, useMemo, useState} from 'react';
import CustomModal from '../../../../../../../../elements/CustomModal/CustomModal';
import {makeStyles, Grid, Typography, MenuItem} from '@material-ui/core';
import {colors} from '../../../../../../../../assets/styles/colors';
import {useStoreDevices, useWhatsappApiTemplates} from '../../../../../utils';
import {useAlert} from '../../../../../../../../elements/Alert/AlertContext';
import {openConversationService} from '../../../../../promises/conversation';
import InlineLoader from '../../../../../../../../elements/LoadingSpinner/InlineLoader';
import CustomSelect from '../../../../../../../../elements/CustomSelect/CustomSelect';
import Collapsible from '../../../../../../../../elements/Collapsible/Collapsible';
import WhatsAppTemplateRenderer from '../../../../../../../../elements/whatsapp-template-renderer';
import CustomButton from '../../../../../../../../elements/CustomButton/CustomButton';
import {
  getConversationDisplayName,
  useCurrentConversation,
} from '../../../../../utils/conversation';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '600px',
    position: 'relative',
  },
  paper: {
    padding: '.5rem',
    [theme.breakpoints.up('sm')]: {
      padding: '.75rem',
    },
    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1,
    top: 0,
    left: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: colors.grey900,
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  collapsible: {
    margin: '0 !important',
  },
}));

type Props = {
  onSubmitSuccess: () => void,
};

function getErrorMessage(error: string) {
  if (error === 'CrmMessagingContactAlreadyExistError') {
    return 'El contacto ya existe';
  }

  return 'Ocurrió un error';
}

export default function WhatsApiTemplateModal(props: Props) {
  const {onSubmitSuccess} = props;
  const classes = useStyles();

  const {storeDevices} = useStoreDevices();
  const {showAlert} = useAlert();

  const {currentConversation} = useCurrentConversation();

  const deviceSelected = useMemo(() => {
    if (!currentConversation?.deviceId) return null;

    return (
      storeDevices.find(
        device =>
          device.id === currentConversation.deviceId &&
          device.state === 'CONNECTED' &&
          device.service === 'WHATSAPP_API'
      ) || null
    );
  }, [currentConversation.deviceId, storeDevices]);

  const [templateComponents, setTemplateComponents] = useState([]);
  const {templates, isLoading} = useWhatsappApiTemplates(deviceSelected?.id);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const selectedTemplate = useMemo(() => {
    return templates?.find(template => template.name === selectedTemplateId);
  }, [selectedTemplateId, templates]);

  const onSubmit = data => {
    if (!currentConversation) {
      showAlert('No hay una conversación activa', {
        type: 'warning',
      });

      return;
    }
    if (!selectedTemplate || !deviceSelected) {
      showAlert(
        !deviceSelected
          ? 'No hay un dispositivo WhatsApp API conectado para esta conversación'
          : 'Debe seleccionar un template',
        {
          type: 'error',
        }
      );
      return;
    }

    openConversationService({
      contactInformation: {
        fullName: getConversationDisplayName(currentConversation),
      },
      message: {
        components:
          templateComponents.length > 0 ? templateComponents : undefined,
        template: selectedTemplate.name,
        templateLang: selectedTemplate.language,
      },
      deviceId: deviceSelected.id,
      identifier: currentConversation.identifier,
      provider: 'WHATSAPP_API',
    })
      .then(() => {
        onSubmitSuccess();
      })
      .catch(error => {
        console.log(error);
        const message = getErrorMessage(error?.error?.name);
        showAlert(message, {
          type: 'error',
        });
      });
  };

  const handleTemplateVariablesChange = useCallback(result => {
    setTemplateComponents(result);
  }, []);

  return (
    <CustomModal
      open
      onClose={onSubmitSuccess}
      classes={{
        paper: classes.paper,
      }}
    >
      <section className={classes.root}>
        {isLoading && (
          <InlineLoader className={classes.loader} loading size={40} />
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.title}>Enviar mensaje</Typography>
          </Grid>

          <Grid item xs={12}>
            <CustomSelect
              fullWidth
              value={selectedTemplateId}
              onChange={e => {
                setSelectedTemplateId(e.target.value);
              }}
              label="Template"
              disabled={!deviceSelected}
            >
              {templates.map(template => (
                <MenuItem key={template?.id} value={template?.name}>
                  <Typography>{template?.name}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
          {selectedTemplate && (
            <Grid item xs={12}>
              <Collapsible
                className={classes.collapsible}
                title="Visualizar template"
              >
                <WhatsAppTemplateRenderer
                  template={selectedTemplate}
                  onChangeVariables={handleTemplateVariablesChange}
                />
              </Collapsible>
            </Grid>
          )}

          <Grid item xs={12}>
            <CustomButton
              variant="contained"
              fullWidth
              color="primary"
              onClick={onSubmit}
              disabled={!deviceSelected || !selectedTemplateId}
            >
              Enviar mensaje
            </CustomButton>
          </Grid>
        </Grid>
      </section>
    </CustomModal>
  );
}
